import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { useGetBetHistoryQuery } from '@/store/api/betReportApi';
import type { BetHistoryBet } from '@/types/betReportTypes';
import { cn, formatCurrency, formatDate, getBetOnDisplayLabel, getNumberValue } from '@/utils';
import EmptyState from '@/components/common/EmptyState';
import { IoCloseSharp } from 'react-icons/io5';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { closeMyBetsModal } from '@/store/slices/currentBetCountSlice';
import MyBetsParlayList from './MyBetsParlayList';
import { isExchangeBet } from '@/utils/betReportGuards';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';

const LIMIT = 100;

const getBetId = (row: BetHistoryBet, index: number) => String(row.id ?? `row-${index}`);
const getEventName = (row: BetHistoryBet) => row.event?.name ?? 'Match';
const getMarketName = (row: BetHistoryBet) => row.marketName ?? '--';

export default function MyBetsModal() {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<'exchange' | 'parlay'>('exchange');
  const [selectedBet, setSelectedBet] = useState<BetHistoryBet | null>(null);
  const { normalPendingCount, parlayPendingCount } = useAppSelector(
    (state) => state.currentBetCount
  );
  const { data, isFetching } = useGetBetHistoryQuery({
    page: 1,
    limit: LIMIT,
    betTime: 'CURRENT',
  });

  const onClose = () => dispatch(closeMyBetsModal());

  const bets = useMemo(() => (data?.bets ?? []).filter(isExchangeBet), [data?.bets]);
  const exchangeCount = normalPendingCount;

  return (
    <div className="mt-[6.875rem] fixed left-0 right-0 bottom-0 z-[90] h-[calc(100dvh-6.875rem)] flex items-end justify-center">
      <div className="flex h-full w-full max-w-[30rem] flex-col rounded-t-xl border-x-4 border-black bg-white text-black shadow-2xl">
        <header className="relative flex min-h-[3.375rem] shrink-0 items-center justify-center rounded-t-lg bg-black px-12 text-white">
          {selectedBet && (
            <button
              type="button"
              onClick={() => setSelectedBet(null)}
              className="absolute left-2 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center"
              aria-label="Back to my bets"
            >
              <i className="icon-arrow-down rotate-90 text-base" />
            </button>
          )}
          {selectedBet ? (
            <div className="break-words text-center">
              <span className="text-[1rem] font-medium">{getEventName(selectedBet)}</span>
              <i className={'icon-triangle-black-600 text-[0.02rem] align-middle'} />
              <span className="text-[0.98rem] text-gray-300">{getMarketName(selectedBet)}</span>
            </div>
          ) : (
            <h1 className="min-w-0 truncate text-[1.075rem] font-medium">My Bets</h1>
          )}
          <button
            type="button"
            onClick={onClose}
            className="absolute right-2 top-1/2 flex size-10 -translate-y-1/2 items-center justify-center"
            aria-label="Close my bets"
          >
            <IoCloseSharp strokeWidth={3} className="text-[1.6rem]" />
          </button>
        </header>

        {selectedBet ? (
          <BetDetail bet={selectedBet} />
        ) : (
          <>
            <div className="relative grid h-[3.313rem] shrink-0 grid-cols-2 items-center rounded-t-lg bg-[#d3e1e8] text-center text-[1.075rem] font-medium">
              <button
                type="button"
                onClick={() => setActiveTab('exchange')}
                className="flex h-full items-center justify-center"
              >
                Exchange <CountBadge>{exchangeCount}</CountBadge>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('parlay')}
                className="flex h-full items-center justify-center"
              >
                Multibet <CountBadge>{parlayPendingCount}</CountBadge>
              </button>
              <span
                className={cn(
                  'pointer-events-none absolute bottom-1 h-1 w-[4.5rem] rounded-full bg-black transition-[left] duration-200',
                  activeTab === 'exchange'
                    ? 'left-[25%] -translate-x-1/2'
                    : 'left-[75%] -translate-x-1/2'
                )}
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto bg-white">
              {activeTab === 'parlay' ? (
                <MyBetsParlayList />
              ) : isFetching ? (
                <div className="space-y-2 p-3">
                  {Array.from({ length: 4 }).map((_, index) => (
                    <div key={index} className="h-[3.625rem] animate-pulse rounded bg-[#eef4f7]" />
                  ))}
                </div>
              ) : bets.length ? (
                bets.map((bet, index) => (
                  <button
                    key={getBetId(bet, index)}
                    type="button"
                    onClick={() => setSelectedBet(bet)}
                    className="flex gap-2 min-h-[3.938rem] w-full items-center border-b border-[#d1dde4] px-3 text-left"
                  >
                    <span className="shrink-0 flex size-7 items-center justify-center rounded bg-primary text-[0.938rem] font-bold">
                      {index + 1}
                    </span>
                    <div className="min-w-0">
                      <span className="break-words text-[1rem] font-medium">
                        {getEventName(bet)}
                      </span>
                      <i className={'icon-triangle-black-600 text-[0.02rem] align-middle'} />
                      <span className="break-words text-[1rem] text-gray-500">
                        {getMarketName(bet)}
                      </span>
                      {bet.isBonusBet && (
                        <span className="ml-1.5 rounded-full bg-primary px-2 py-[0.1rem] text-[0.688rem] font-bold leading-none text-black">
                          Bonus Bet
                        </span>
                      )}
                    </div>
                    <span className="shrink-0 ml-auto flex size-7 items-center justify-center rounded bg-[#dce8ee]">
                      <i className="icon-arrow-down -rotate-90 text-[0.8rem]" />
                    </span>
                  </button>
                ))
              ) : (
                <EmptyState className="pt-[8.125rem]" />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CountBadge({ children }: { children: ReactNode }) {
  return (
    <span className="ml-1 inline-flex h-6 min-w-6 items-center justify-center rounded bg-black px-1 text-[1rem] text-white">
      {children}
    </span>
  );
}

function FilterBar() {
  return (
    <div className="flex h-[3.375rem] shrink-0 items-center justify-between bg-[#eef7fb] px-3">
      <button className="flex h-9 min-w-[9.625rem] items-center justify-between rounded bg-white px-3 text-[1rem] shadow-sm">
        <span>↕ by Time</span>
        <ChevronDown size={18} fill="black" />
      </button>
      <button className="flex h-9 min-w-[3.875rem] items-center justify-between rounded bg-white px-3 text-[1rem] shadow-sm">
        <span>ALL</span>
        <ChevronDown size={18} />
      </button>
    </div>
  );
}

function BetDetail({ bet }: { bet: BetHistoryBet }) {
  const currencySymbol = useCurrencySymbol();
  const betOn = String(bet.betOn ?? '').toLowerCase();
  const betOnLabel = getBetOnDisplayLabel(betOn, bet.marketType);
  const selection = bet.selection ?? '-';
  const odds = bet.odds ?? '0';
  const stake = getNumberValue(bet.amount);
  const profit = getNumberValue(bet.potentialProfit);
  const placedAt = bet.placedAt ?? '';

  return (
    <>
      {/* <FilterBar /> */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-white">
        {/* <div className="flex h-[3.375rem] items-center justify-center bg-[#d3e1e8]">
          <span className="border-b-4 pb-1 border-black text-[1.075rem] font-bold">
            Matched <CountBadge>0</CountBadge>
          </span>
        </div> */}

        <article className="m-3 overflow-hidden rounded border border-[#cbd9e0] bg-white">
          <div className="flex h-[3.25rem] items-center gap-3 bg-[#eef7fb] px-3">
            <span
              className={cn(
                'rounded-full px-4 py-2 text-[1.063rem] font-medium leading-none',
                betOn === 'lay' ? 'bg-lay' : 'bg-back'
              )}
            >
              {betOnLabel}
            </span>
            <strong className="min-w-0 truncate text-[1.075rem]">{selection}</strong>
          </div>
          <div className="grid grid-cols-3 border-y border-[#cbd9e0]">
            <InfoCell label="Odds" value={odds} />
            <InfoCell label={`Stake (${currencySymbol})`} value={formatCurrency(stake, '-', 2)} />
            <InfoCell label={`Profit (${currencySymbol})`} value={formatCurrency(profit, '-', 2)} />
          </div>
          <div className="flex min-h-[2.063rem] items-center justify-between px-3 text-[0.875rem] text-[#58758a]">
            <span className="inline-flex items-center gap-1.5">
              Ref: {getBetId(bet, 0)}
              {bet.isBonusBet && (
                <span className="rounded-full bg-primary px-2 py-[0.1rem] text-[0.7rem] font-bold leading-none text-black">
                  Bonus Bet
                </span>
              )}
            </span>
            <span>{formatDate(placedAt, 'DD-MM-YYYY HH:mm', '')}</span>
          </div>
        </article>
      </div>
    </>
  );
}

function InfoCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-h-[3.625rem] border-r border-[#cbd9e0] px-3 py-2 last:border-r-0">
      <p className="text-[0.813rem]">{label}</p>
      <strong className="block text-[1.075rem] leading-tight">{value}</strong>
    </div>
  );
}
